/**
 * CreditBalanceCard — surfaces the credit balance + top-up CTAs.
 *
 * v1 reference: ``app/components/billing/CreditBalanceCard.tsx``. Wires
 * to `GET /billing/credits` + `GET /billing/credits/products` + `POST
 * /billing/credits/purchase` — all three already typed in
 * `@ando/core/api/endpoints/billing.ts`.
 *
 * UX:
 *  - Big tabular-nums balance reading. Two-line meta below ("ultima ricarica
 *    Xd fa · usato Y questo mese").
 *  - Topup grid: 3-4 packs selectable, current pack highlighted in orange.
 *  - "Acquista" CTA opens Stripe checkout via the BE-issued URL.
 *  - Auto-topup toggle (BE flag, read-only display for round 2 — editing
 *    moves to round 3).
 */

import { Loader2, Sparkles, Zap } from "lucide-react";
import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Trans, useLingui } from "@lingui/react/macro";

import {
  getBillingCreditBalance,
  getBillingTopupProducts,
  purchaseBillingCredits,
  type BillingTopupProduct,
} from "@ando/core";
import { Button, cn } from "@ando/ui";

function formatCredits(n: number): string {
  if (n >= 10_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toLocaleString();
}

export function CreditBalanceCard(): React.ReactElement {
  const { t } = useLingui();
  const balanceQ = useQuery({
    queryKey: ["billing", "credit-balance"],
    queryFn: () => getBillingCreditBalance(),
    staleTime: 30_000,
  });
  const productsQ = useQuery({
    queryKey: ["billing", "topup-products"],
    queryFn: () => getBillingTopupProducts(),
    staleTime: 5 * 60_000,
  });

  const [selectedId, setSelectedId] = useState<number | null>(null);

  const purchase = useMutation({
    mutationFn: (productId: number) =>
      purchaseBillingCredits({
        product_id: productId,
        success_url: `${window.location.origin}/settings/billing?topup=ok`,
        cancel_url: `${window.location.origin}/settings/billing?topup=cancel`,
      }),
    onSuccess: (data) => {
      if (data.checkout_url) {
        window.location.assign(data.checkout_url);
      }
    },
  });

  return (
    <section
      aria-labelledby="credit-balance-title"
      className="rounded-xl border border-hairline bg-card p-5"
    >
      <header className="mb-4 flex items-baseline justify-between gap-3">
        <h3
          id="credit-balance-title"
          className="font-semibold text-[13px] text-ink-1 uppercase tracking-wide"
        >
          <Trans>Credito disponibile</Trans>
        </h3>
        {balanceQ.data?.auto_topup_enabled ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-ando-orange-soft px-2 py-0.5 font-medium text-[10px] text-ando-orange">
            <Sparkles className="h-3 w-3" aria-hidden />
            <Trans>Auto-ricarica</Trans>
          </span>
        ) : null}
      </header>

      {balanceQ.isLoading ? (
        <div className="flex items-center gap-2 text-[12px] text-ink-3">
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          <Trans>Caricamento…</Trans>
        </div>
      ) : balanceQ.data ? (
        <>
          <div className="font-semibold text-3xl text-ink-1 tabular-nums">
            {formatCredits(balanceQ.data.balance)}{" "}
            <span className="font-normal text-[14px] text-ink-3">
              <Trans>crediti</Trans>
            </span>
          </div>
          <p className="mt-1 text-[11px] text-ink-3 tabular-nums">
            <Trans>
              Acquistati totali: {balanceQ.data.lifetime_purchased.toLocaleString()} · usati:{" "}
              {balanceQ.data.lifetime_used.toLocaleString()}
            </Trans>
          </p>
        </>
      ) : (
        <p className="text-[12px] text-ink-3">
          <Trans>Bilancio non disponibile.</Trans>
        </p>
      )}

      <div className="mt-5">
        <h4 className="mb-2 font-medium text-[11px] text-ink-2 uppercase tracking-wide">
          <Trans>Pacchetti</Trans>
        </h4>
        {productsQ.isLoading ? (
          <p className="text-[11px] text-ink-3">
            <Trans>Caricamento pacchetti…</Trans>
          </p>
        ) : productsQ.data && productsQ.data.length > 0 ? (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {productsQ.data.map((p: BillingTopupProduct) => {
              const active = selectedId === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setSelectedId(p.id)}
                  aria-pressed={active}
                  className={cn(
                    "flex flex-col items-start gap-0.5 rounded-md border px-3 py-2.5 text-left transition-colors",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange focus-visible:ring-offset-1",
                    active
                      ? "border-ando-orange bg-ando-orange-soft"
                      : "border-hairline hover:border-ink-3/40",
                  )}
                >
                  <span className="font-semibold text-[12px] text-ink-1">{p.name}</span>
                  <span className="text-[10px] text-ink-3 tabular-nums">
                    <Trans>{p.credits.toLocaleString()} crediti</Trans>
                  </span>
                  <span className="mt-1 font-medium text-[11px] text-ando-orange tabular-nums">
                    € {p.amount.toFixed(2)}
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          <p className="text-[11px] text-ink-3">
            <Trans>Pacchetti non disponibili in questo ambiente.</Trans>
          </p>
        )}

        <div className="mt-3 flex items-center justify-end gap-2">
          <Button
            variant="primary"
            size="md"
            disabled={selectedId === null || purchase.isPending}
            onClick={() => selectedId !== null && purchase.mutate(selectedId)}
          >
            <Zap className="h-3.5 w-3.5" aria-hidden />
            {purchase.isPending ? t`Apertura checkout…` : t`Acquista crediti`}
          </Button>
        </div>
      </div>
    </section>
  );
}
CreditBalanceCard.displayName = "CreditBalanceCard";
